import moment from "moment";
import * as DateUtil from "@travellocal/utils/dateHelpers";

export const QSParam = {
  /** integer */
  budget: "budgetpp",

  /** integer */
  currencyId: "currencyId",

  /** integer */
  duration: "duration",

  /** either MM-YYYY or DD-MM-YYYY */
  startDate: "startDate",

  /** string */
  notes: "notes",

  /** comma-sepped integers */
  themes: "themes",

  /** integer 1-4 */
  guiding: "guiding",

  /** integer 1-4 */
  planningStage: "planningStage",

  /** integer 1-4 */
  travelStyle: "travelStyle",

  /** comma-sepped {id}:{count} */
  travellers: "travellers",

  /** id of a sample itinerary, only present if enquire clicked from itinerary page */
  itineraryId: "itineraryId",

  /** id of LTO we want the enquiry to be sent to */
  ltoId: "ltoId",
};

export function buildFormStateFromQueryString(qs: { [key: string]: string }): Forms.EnquiryForm {
  const state = {} as Forms.EnquiryForm;

  if (qs[QSParam.budget]) {
    const budgetpp = parseInt(qs[QSParam.budget], 10);
    if (!isNaN(budgetpp)) {
      state.budget = { budgetPerPerson: budgetpp, budgetUnknown: false };
    }
  }

  if (qs[QSParam.currencyId]) {
    const currencyId = parseInt(qs[QSParam.currencyId], 10);
    if (!isNaN(currencyId)) {
      state.currencyId = currencyId;
    }
  }

  if (qs[QSParam.duration] || qs[QSParam.startDate]) {
    const travelDate = {} as Forms.EnquiryFormTravelDate;
    const asMonth = moment(qs[QSParam.startDate], "MM-YYYY", true);
    if (asMonth.isValid()) {
      travelDate.preferredTravelMonth = {
        month: asMonth.month() + 1, // moment's months are 0-indexed, ours our 1-indexed
        year: asMonth.year(),
      };
    }

    const asDate = moment(qs[QSParam.startDate], "DD-MM-YYYY", true);
    if (asDate.isValid()) {
      travelDate.preferredTravelExactDate = DateUtil.momentToDate(asDate);
    }

    const duration = parseInt(qs[QSParam.duration], 10);
    if (!isNaN(duration) && duration > 0) {
      travelDate.preferredTravelNumberOfDays = duration;
    }

    if (Object.keys(travelDate).length > 0) {
      state.travelDate = travelDate;
    }
  }

  if (qs[QSParam.notes]) {
    state.notes = qs[QSParam.notes];
  }

  if (qs[QSParam.themes]) {
    state.themes = {
      noThemes: false,
      themeIds: qs[QSParam.themes].split(",").map(x => parseInt(x.trim(), 10)).filter(x => !isNaN(x)),
    };
  }

  const guiding = parseInt(qs[QSParam.guiding], 10);
  if (guiding > 0 && guiding < 4) {
    state.independenceLevel = guiding;
  }

  const planningStage = parseInt(qs[QSParam.planningStage], 10);
  if (planningStage > 0 && planningStage < 4) {
    state.planningStage = planningStage;
  }

  const travelStyle = parseInt(qs[QSParam.travelStyle], 10);
  if (travelStyle > 0 && travelStyle < 4) {
    state.tripType = travelStyle;
  }

  if (qs[QSParam.travellers]) {
    const kvs = qs[QSParam.travellers].split(",").map(x => x.trim());
    if (kvs.length > 0) {
      const ages = {};
      for (const kv of kvs) {
        const [key, value] = kv.split(":");
        if (!key || !value) {
          continue;
        }

        const amount = parseInt(value, 10);
        if (isNaN(amount)) {
          continue;
        }

        ages[key] = amount;
      }

      if (Object.keys(ages).length > 0) {
        state.travellers = { ageRangeIds: ages } as Forms.EnquiryFormTravellers;
      }
    }
  }

  if (qs[QSParam.itineraryId]) {
    const id = parseInt(qs[QSParam.itineraryId], 10);
    if (id && !isNaN(id)) {
      state.itineraryId = id;
    }
  }

  if (qs[QSParam.ltoId]) {
    const id = parseInt(qs[QSParam.ltoId], 10);
    if (id && !isNaN(id)) {
      state.ltoId = id;
    }
  }

  return state;
}
